import React, { FC, useCallback } from 'react'
import styles from 'styles/components/template.module.scss'
import text from 'styles/utility/text.module.scss'
import classNames from 'classnames'
import { push } from 'connected-react-router'
import { State } from 'index'
import { useDispatch, useSelector } from 'react-redux'
import { Spacer } from 'components/Utility'
import { BlueButton, GreyButton } from 'components/Button'
import { Description, MainHeadline } from 'components/Typography'
import { CautionCard, MonthlyReportCard } from 'components/Card'
import { MIN_TRANSFERABLE_AMOUNT } from 'constants/stripe'
import { getStoreRef } from 'reducks/auth/selectors'
import { useQueryMonthlyReport, useQueryShopBalance } from 'hooks/revenue'
import { getShopRef } from 'reducks/store/selectors'
import { FlexRow } from '../../../Flex'

type Props = {
  headline: string
  datetime: Date
  yearMonth: string | undefined
}

const MonthlyReportDetailTemplate: FC<Props> = (props) => {
  const { headline, datetime, yearMonth } = props
  const dispatch = useDispatch()
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const shopRef = useSelector((state: State) => getShopRef(state))
  const year = yearMonth ? Number(yearMonth.slice(0, 4)) : undefined
  const month = yearMonth ? Number(yearMonth.slice(4, 6)) : undefined
  // レポートの値を展開
  const { report, hasError } = useQueryMonthlyReport(storeRef.id, year, month)
  const couponAmount = report?.report.coupon?.amount || 0
  const amount = report?.report.amount || 0
  const feeAmount = report?.report.fee_amount || 0
  const total = amount - feeAmount + couponAmount
  // 残高照会
  const balance = useQueryShopBalance(shopRef?.id)
  const isAbleToTransfer = balance.availableAmount >= total && total > MIN_TRANSFERABLE_AMOUNT
  const moveToTransfer = useCallback(() => {
    dispatch(push(`/account/revenue/bank_transfer?amount=${total}`))
  }, [total])
  const backToMonthlyReport = useCallback(() => dispatch(push('/account/revenue/monthly_report')), [])
  const printMonthlyReport = useCallback(() => {
    dispatch(push(`/account/revenue/monthly_report/print/${yearMonth}`))
  }, [yearMonth])
  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={headline} />
      <Spacer size={28} />
      <BlueButton disabled={hasError} label={'印刷する'} onClick={printMonthlyReport} size={'medium'} type={'main'} />
      <Spacer size={12} />
      {hasError ? (
        <CautionCard
          caution={'売上レポートを取得できませんでした'}
          description={
            'お手数ですが、時間をおいて再度お試しください。\n' +
            '解消しない場合は下記のお問い合わせ先までご連絡ください。'
          }
          size={'small'}
        />
      ) : (
        <>
          <Description
            description={
              'いつもお引き立てを賜り厚く御礼申し上げます。\n' +
              '当該期間における貴店のsacriでの売上は下記の通りとなっております。'
            }
          />
          <Spacer size={24} />
          <MonthlyReportCard
            amount={amount}
            couponAmount={couponAmount}
            datetime={datetime}
            feeAmount={feeAmount}
            storeNames={report?.store_names || []}
            taxBreakdown={report?.report.tax_breakdown}
            total={total}
          />
          <Spacer size={12} />
          <Description
            description={
              'この度はご利用頂き誠にありがとうございました。\n' +
              '引き続き、sacriを何卒宜しくお願い致します。\n' +
              '※貴店売上よりクーポン利用による補填金額を還付し、sacri手数料及び手数料に関わる消費税を引いた金額が手数料控除後売上額となります。'
            }
          />
        </>
      )}
      <Spacer size={12} />
      <Description description={'本レポートに関するお問い合わせ先'} />
      <a
        className={classNames(text.color_blue_main)}
        href={'mailto:support@sacrisupport.zendesk.com'}
        target={'_blank'}
        rel={'noopener noreferrer'}
      >
        support@sacrisupport.zendesk.com
      </a>
      <Spacer size={28} />
      <BlueButton
        disabled={!isAbleToTransfer}
        label={'この金額を入金する'}
        onClick={moveToTransfer}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={28} />
      <GreyButton
        label={'月次レポート一覧ページに戻る'}
        onClick={backToMonthlyReport}
        size={'medium'}
        type={'outlined'}
      />
      <Spacer size={72} />
    </div>
  )
}

export default MonthlyReportDetailTemplate
